import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, type ApiResponse } from "@/api/client";
import {
  Mail,
  Users,
  Send,
  Plus,
  Search,
  Calendar,
  BarChart3,
  Eye,
  Edit,
  Trash2,
} from "lucide-react";
import { formatDate } from "@ckgo2/shared/utils";
import toast from "react-hot-toast";

interface NewsletterSubscriber {
  id: string;
  email: string;
  name?: string;
  status: "active" | "unsubscribed";
  source: string;
  createdAt: string;
}

interface NewsletterCampaign {
  id: string;
  subject: string;
  status: "draft" | "scheduled" | "sent";
  sentAt?: string;
  scheduledFor?: string;
  recipientCount?: number;
  openRate?: number;
  clickRate?: number;
  createdAt: string;
}

export default function NewsletterPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<"campaigns" | "subscribers">("campaigns");
  const [search, setSearch] = useState("");

  const { data: subscribersData, isLoading: loadingSubscribers } = useQuery({
    queryKey: ["newsletter-subscribers"],
    queryFn: () => api.get<ApiResponse<NewsletterSubscriber[]>>("/api/admin/newsletter/subscribers"),
  });

  const { data: campaignsData, isLoading: loadingCampaigns } = useQuery({
    queryKey: ["newsletter-campaigns"],
    queryFn: () => api.get<ApiResponse<NewsletterCampaign[]>>("/api/admin/newsletter/campaigns"),
  });

  const subscribers = subscribersData?.data || [];
  const campaigns = campaignsData?.data || [];

  const activeSubscribers = subscribers.filter((s) => s.status === "active");

  // Filter based on search
  const filteredSubscribers = subscribers.filter((s) =>
    s.email.toLowerCase().includes(search.toLowerCase()) ||
    s.name?.toLowerCase().includes(search.toLowerCase())
  );

  const filteredCampaigns = campaigns.filter((c) =>
    c.subject.toLowerCase().includes(search.toLowerCase())
  );

  // Delete campaign mutation
  const deleteCampaignMutation = useMutation({
    mutationFn: (campaignId: string) =>
      api.delete(`/api/admin/newsletter/campaigns/${campaignId}`),
    onSuccess: () => {
      toast.success("Kampaň smazána");
      queryClient.invalidateQueries({ queryKey: ["newsletter-campaigns"] });
    },
    onError: () => {
      toast.error("Nepodařilo se smazat kampaň");
    },
  });

  // Delete subscriber mutation
  const deleteSubscriberMutation = useMutation({
    mutationFn: (subscriberId: string) =>
      api.delete(`/api/admin/newsletter/subscribers/${subscriberId}`),
    onSuccess: () => {
      toast.success("Odběratel smazán");
      queryClient.invalidateQueries({ queryKey: ["newsletter-subscribers"] });
    },
    onError: () => {
      toast.error("Nepodařilo se smazat odběratele");
    },
  });

  const handleDeleteCampaign = (id: string) => {
    if (window.confirm("Opravdu chcete smazat tuto kampaň?")) {
      deleteCampaignMutation.mutate(id);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Newsletter
          </h1>
          <p className="mt-1 text-gray-600 dark:text-gray-400">
            Správa odběratelů a kampaní
          </p>
        </div>
        <button
          onClick={() => navigate("/newsletter/editor/new")}
          className="btn-primary"
        >
          <Plus className="h-5 w-5" />
          Nová kampaň
        </button>
      </div>

      {/* Stats */}
      <div className="grid gap-4 sm:grid-cols-4">
        <div className="card">
          <div className="flex items-center gap-3">
            <div className="rounded-full bg-primary-100 p-3 text-primary-600 dark:bg-primary-900/30 dark:text-primary-400">
              <Users className="h-6 w-6" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Odběratelé
              </p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {activeSubscribers.length}
              </p>
            </div>
          </div>
        </div>
        <div className="card">
          <div className="flex items-center gap-3">
            <div className="rounded-full bg-success-100 p-3 text-success-600 dark:bg-success-900/30 dark:text-success-400">
              <Send className="h-6 w-6" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Odeslané kampaně
              </p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {campaigns.filter((c) => c.status === "sent").length}
              </p>
            </div>
          </div>
        </div>
        <div className="card">
          <div className="flex items-center gap-3">
            <div className="rounded-full bg-accent-100 p-3 text-accent-600 dark:bg-accent-900/30 dark:text-accent-400">
              <Eye className="h-6 w-6" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Průměrné otevření
              </p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {campaigns.length > 0
                  ? Math.round(
                      campaigns
                        .filter((c) => c.openRate)
                        .reduce((sum, c) => sum + (c.openRate || 0), 0) /
                        campaigns.filter((c) => c.openRate).length || 0
                    )
                  : 0}
                %
              </p>
            </div>
          </div>
        </div>
        <div className="card">
          <div className="flex items-center gap-3">
            <div className="rounded-full bg-warning-100 p-3 text-warning-600 dark:bg-warning-900/30 dark:text-warning-400">
              <BarChart3 className="h-6 w-6" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Průměrné prokliky
              </p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {campaigns.length > 0
                  ? Math.round(
                      campaigns
                        .filter((c) => c.clickRate)
                        .reduce((sum, c) => sum + (c.clickRate || 0), 0) /
                        campaigns.filter((c) => c.clickRate).length || 0
                    )
                  : 0}
                %
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="card">
        <div className="flex border-b border-gray-200 dark:border-gray-700">
          <button
            onClick={() => setActiveTab("campaigns")}
            className={`px-4 py-3 text-sm font-medium transition-colors ${
              activeTab === "campaigns"
                ? "border-b-2 border-primary-500 text-primary-600 dark:text-primary-400"
                : "text-gray-500 hover:text-gray-700 dark:text-gray-400"
            }`}
          >
            <div className="flex items-center gap-2">
              <Mail className="h-4 w-4" />
              Kampaně
            </div>
          </button>
          <button
            onClick={() => setActiveTab("subscribers")}
            className={`px-4 py-3 text-sm font-medium transition-colors ${
              activeTab === "subscribers"
                ? "border-b-2 border-primary-500 text-primary-600 dark:text-primary-400"
                : "text-gray-500 hover:text-gray-700 dark:text-gray-400"
            }`}
          >
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              Odběratelé ({activeSubscribers.length})
            </div>
          </button>
        </div>

        {/* Search */}
        <div className="mt-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder={
                activeTab === "campaigns"
                  ? "Hledat kampaně..."
                  : "Hledat odběratele..."
              }
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="input pl-10"
            />
          </div>
        </div>

        {/* Content */}
        <div className="mt-4">
          {activeTab === "campaigns" ? (
            loadingCampaigns ? (
              <div className="flex h-32 items-center justify-center">
                <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-500 border-t-transparent" />
              </div>
            ) : filteredCampaigns.length === 0 ? (
              <div className="flex h-32 flex-col items-center justify-center text-gray-500">
                <Mail className="mb-2 h-8 w-8 text-gray-300" />
                <p>Žádné kampaně</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-200 dark:divide-gray-700">
                {filteredCampaigns.map((campaign) => (
                  <div
                    key={campaign.id}
                    className="flex items-center justify-between py-4"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <h3 className="font-medium text-gray-900 dark:text-white">
                          {campaign.subject}
                        </h3>
                        {campaign.status === "draft" && (
                          <span className="badge bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                            Koncept
                          </span>
                        )}
                        {campaign.status === "scheduled" && (
                          <span className="badge bg-warning-100 text-warning-700 dark:bg-warning-900/30 dark:text-warning-400">
                            Naplánováno
                          </span>
                        )}
                        {campaign.status === "sent" && (
                          <span className="badge bg-success-100 text-success-700 dark:bg-success-900/30 dark:text-success-400">
                            Odesláno
                          </span>
                        )}
                      </div>
                      <div className="mt-1 flex items-center gap-4 text-sm text-gray-500">
                        <span className="flex items-center gap-1">
                          <Calendar className="h-4 w-4" />
                          {campaign.sentAt
                            ? formatDate(campaign.sentAt)
                            : campaign.scheduledFor
                              ? `Naplánováno: ${formatDate(campaign.scheduledFor)}`
                              : formatDate(campaign.createdAt)}
                        </span>
                        {campaign.recipientCount && (
                          <span className="flex items-center gap-1">
                            <Users className="h-4 w-4" />
                            {campaign.recipientCount} příjemců
                          </span>
                        )}
                        {campaign.openRate !== undefined && (
                          <span className="flex items-center gap-1">
                            <Eye className="h-4 w-4" />
                            {campaign.openRate}% otevřeno
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => navigate(`/newsletter/editor/${campaign.id}`)}
                        className="btn-ghost btn-sm"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteCampaign(campaign.id)}
                        disabled={campaign.status === "sent"}
                        className="btn-ghost btn-sm text-error-600 hover:bg-error-50 disabled:opacity-50"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )
          ) : loadingSubscribers ? (
            <div className="flex h-32 items-center justify-center">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-500 border-t-transparent" />
            </div>
          ) : filteredSubscribers.length === 0 ? (
            <div className="flex h-32 flex-col items-center justify-center text-gray-500">
              <Users className="mb-2 h-8 w-8 text-gray-300" />
              <p>Žádní odběratelé</p>
            </div>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700">
                  <th className="pb-3 text-left text-sm font-medium text-gray-500">
                    Email
                  </th>
                  <th className="pb-3 text-left text-sm font-medium text-gray-500">
                    Jméno
                  </th>
                  <th className="pb-3 text-left text-sm font-medium text-gray-500">
                    Zdroj
                  </th>
                  <th className="pb-3 text-left text-sm font-medium text-gray-500">
                    Registrace
                  </th>
                  <th className="pb-3 text-left text-sm font-medium text-gray-500">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {filteredSubscribers.map((subscriber) => (
                  <tr key={subscriber.id}>
                    <td className="py-3">
                      <span className="text-gray-900 dark:text-white">
                        {subscriber.email}
                      </span>
                    </td>
                    <td className="py-3">
                      <span className="text-gray-600 dark:text-gray-400">
                        {subscriber.name || "-"}
                      </span>
                    </td>
                    <td className="py-3">
                      <span className="badge text-xs">{subscriber.source}</span>
                    </td>
                    <td className="py-3">
                      <span className="text-sm text-gray-500">
                        {formatDate(subscriber.createdAt)}
                      </span>
                    </td>
                    <td className="py-3">
                      {subscriber.status === "active" ? (
                        <span className="badge bg-success-100 text-success-700 dark:bg-success-900/30 dark:text-success-400">
                          Aktivní
                        </span>
                      ) : (
                        <span className="badge bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                          Odhlášen
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
